/**
 * The managed-login-flow rules, as pure functions — the `auth.flow.*`
 * counterpart to auth.ts, and shared for the same reason: a widget author builds
 * against the mock host and ships against the real bridge, so anything both
 * hosts have to agree on lives here rather than in either one.
 *
 * What is *not* here: anything that performs the flow. Starting the
 * authorization request, holding the Keycloak cookie jar and the opaque
 * continuation URL, following the final redirect, exchanging the code and
 * signing the customer in are host mechanics by definition — the widget never
 * sees any of them (see the auth flow section of contracts.ts).
 */

import { AppAuthFlowField, AppAuthFlowState, AppAuthFlowStep } from './contracts';

/**
 * The step types this version of the contract describes — the ones documented
 * on `AppAuthFlowStep.metadata` and in the authoring guide.
 *
 * It is **not** the set a project can produce. The flow is configured in
 * Keycloak, so a realm can start returning a step no build of this SDK has heard
 * of; that is why `AppAuthFlowStep.type` is an open string and why the rule
 * below is fail-closed rather than exhaustive-match.
 */
export const AUTH_FLOW_KNOWN_STEP_TYPES = [
  'username_password',
  'username',
  'password',
  'pin',
  'sms_otp',
  'email_otp',
  'totp',
  'totp_setup',
  'phone_number',
  'update_password',
  // Native reset-credentials journey: identify, email-link waiting, then new password.
  'reset_credentials_identify',
  'reset_email_sent',
  'reset_password',
  // The FINx1 default flow (docs/platform/authentication/default-login-flow.md), proven by
  // tools/login-flow-harness on 2026-08-30: `identify` is the extension's username form
  // (finxone-auth-username-form), `create_pin` / `reset_pin` are pin-authenticator's set and
  // recovery steps, `verify_identity` is user-attributes-authenticator (date of birth + postcode).
  'identify',
  'create_pin',
  'reset_pin',
  'verify_identity',
  // Tier 1b. Purely additive, and additive in both directions: the rule below is
  // fail-closed, so a build that predates this entry renders the step as a dead end
  // rather than guessing at it. The gateway reads the same list — `publicStep` in
  // `app-session.service.ts` rejects a step type this array does not carry — so the
  // entry is also what lets the step reach an application at all.
  'device_signature',
  // Headless plugin catalogue (docs/headless-login-ui-guide.md). Recognition is a
  // wire-contract guarantee, not permission to render every step as a text form.
  'webauthn',
  'webauthn_register',
  'passkey',
  'passkey_register',
  'passkey_conditional',
  'identity_provider',
  'broker_review_profile',
  'broker_verify_email',
  'broker_confirm_link',
  'broker_confirm_override',
  'broker_reauthenticate',
  'consent',
  'registration',
  'organization',
  'recovery_code',
  'recovery_codes_setup',
  'security_questions',
  'update_profile',
  'verify_profile',
  'verify_email',
  'update_email',
  'update_email_verification',
  'update_locale',
  'delete_credential',
  'delete_account',
  'account_deleted',
  'accept_terms',
] as const;

export type AppAuthFlowKnownStepType = (typeof AUTH_FLOW_KNOWN_STEP_TYPES)[number];

/**
 * Whether this SDK version describes the step type.
 *
 * **The fail-closed rule.** `false` means render a dead end — an explanatory
 * message plus a restart (and any alternative the step itself lists) — and
 * nothing else. Never fall back to "render `fields` generically and hope": the
 * fields of an unknown step are unknown by construction, so a generic render is
 * a text box a customer types a credential into against a step whose meaning the
 * widget could not establish. A dead end costs one login attempt; a guess can
 * cost the credential.
 */
export function isKnownAuthFlowStepType(type: string): type is AppAuthFlowKnownStepType {
  return (AUTH_FLOW_KNOWN_STEP_TYPES as readonly string[]).includes(type);
}

/** Narrows a flow state to the step case, so a widget branches on `status` once. */
export function isAuthFlowStep(state: AppAuthFlowState): state is AppAuthFlowStep {
  return state.status === 'interaction-required';
}

/**
 * The message both hosts reject an unoffered alternative with.
 *
 * Shared because the rule is shared: only the names in the current step's
 * `actions` may be submitted. Sending one the step did not offer is a widget
 * bug — the server answers `action_not_available` for it — and both hosts say so
 * with the same sentence rather than letting the mock be the lenient one.
 */
export function authFlowActionUnavailableMessage(operation: string, action: string): string {
  return (
    `${operation}: '${action}' is not one of the current step's actions. Submit only names the ` +
    'step lists in `actions` — the alternatives available on a step are decided by the flow, not ' +
    'by the widget.'
  );
}

/**
 * The message both hosts reject a submit with when no flow is in progress.
 * `auth.flow.start` mints the transaction; there is nothing to submit to before
 * it, and nothing to resume after a `cancel` or a completion.
 */
export function authFlowNotStartedMessage(operation: string): string {
  return (
    `${operation}: no login flow is in progress. Call auth.flow.start first — a transaction that ` +
    'has been completed or cancelled cannot be resumed.'
  );
}

/* ───────────────────────── interpreting a step ────────────────────────── */

/*
 * The helpers below read a step the realm actually sends and answer the
 * questions a login screen has to get right before it draws anything. They
 * carry no copy and render nothing: they encode what the DF Capital build
 * learned from the live realm (lessons/df-capital/guides/auth-and-session.md,
 * "Rules for rendering steps") so the next application does not re-learn it
 * from a customer who could not type a six-digit code into four boxes.
 */

/**
 * How many boxes a one-time-code field draws.
 *
 * A declared length is believed only when the realm pins it on **both** sides
 * (`minLength === maxLength`) and it is a plausible code, 4 to 8 digits.
 * Anything else answers `fallback`. The live DF Capital realm publishes
 * `minLength 4, maxLength 10` on a six-digit SMS code — a generic floor and a
 * storage ceiling, neither a statement about the code — and believing the floor
 * drew four boxes a six-digit code could not be typed into (DF Capital
 * `b302e01`, `a9b78da`). A lone bound is therefore never a length; a pinned
 * length of 1 or 12 is not a code either.
 */
export function otpLength(
  field: Pick<AppAuthFlowField, 'minLength' | 'maxLength'> | undefined,
  fallback = 6,
): number {
  const length = field?.minLength;
  if (length === undefined || length !== field?.maxLength) return fallback;
  return Number.isInteger(length) && length >= 4 && length <= 8 ? length : fallback;
}

/**
 * One "try another way" alternative worth offering. `id` is the opaque
 * execution id to pass back to `selectMethod`; `providerId` is the Keycloak
 * authenticator (`pin-authenticator`, `sms-otp-authenticator`, …) the
 * application keys its own copy on when `label` is empty.
 */
export interface VisibleAuthMethod {
  id: string;
  providerId?: string;
  /** The server's label when it sent one; `''` when the realm left it blank and only `providerId` identifies the method. */
  label: string;
  /** Always `false` here — the current method is dropped — kept so the shape mirrors the metadata entry. */
  current: boolean;
}

interface AuthMethodMetadataEntry {
  id?: string;
  providerId?: string;
  label?: string;
  current: boolean;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/**
 * `metadata.authenticationMethods`, read defensively: the gateway passes it
 * through from the realm untouched, so every property is `unknown` until
 * checked. The id lives under `id` or `executionId` (the two spellings the
 * gateway itself accepts, `app-session.service.ts` `publicStep`), the display
 * text under `label` or `displayName`.
 */
function authMethodMetadata(step: AppAuthFlowStep): AuthMethodMetadataEntry[] {
  const raw = step.metadata?.['authenticationMethods'];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): AuthMethodMetadataEntry[] => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    return [
      {
        id: readString(record['id']) ?? readString(record['executionId']),
        providerId: readString(record['providerId']),
        label: readString(record['label']) ?? readString(record['displayName']),
        current: record['current'] === true,
      },
    ];
  });
}

/**
 * The sign-in alternatives a step is worth offering, with the identity the
 * application needs to name them.
 *
 * Two things the live realm does make this more than `step.methods`:
 *
 * - **Every `label` arrives empty.** The real identity sits in
 *   `metadata.authenticationMethods[].providerId`, so that is returned
 *   alongside the opaque id for the application to map to copy. Rendering the
 *   raw methods drew empty bordered boxes (DF Capital `a9b78da`).
 * - **The current method is listed with the others**, flagged `current: true`.
 *   Offering "Use your PIN" on the PIN screen is a no-op link, so it is dropped
 *   (`71aa889`).
 *
 * A method with neither a usable id nor any label, or with an id but no label
 * and no provider id, is dropped rather than drawn blank; duplicates by id
 * collapse to the first. `step.methods` is the authority on what is offered;
 * metadata entries that are not in it are **not** added — the contract says
 * render only what the step lists.
 */
export function visibleAuthMethods(step: AppAuthFlowStep): VisibleAuthMethod[] {
  const metadata = authMethodMetadata(step);
  const seen = new Set<string>();
  const visible: VisibleAuthMethod[] = [];
  for (const method of step.methods ?? []) {
    const id = readString(method.id);
    if (id === undefined || seen.has(id)) continue;
    const meta = metadata.find((entry) => entry.id === id);
    if (meta?.current) continue;
    const label = readString(method.label) ?? meta?.label ?? '';
    const providerId = meta?.providerId;
    if (label === '' && providerId === undefined) continue;
    seen.add(id);
    visible.push({ id, ...(providerId !== undefined ? { providerId } : {}), label, current: false });
  }
  return visible;
}

/**
 * Steps whose one secret is a short code typed into boxes rather than a
 * password typed on a line. The realm declares the field `type: password`,
 * which is right about secrecy and wrong about shape (DF Capital `17d40f1`).
 */
export const AUTH_FLOW_PASSCODE_STEP_TYPES = ['pin', 'create_pin', 'reset_pin'] as const;

/** Steps whose field is a one-time code; a `text` field on one of these is the code. */
const OTP_STEP_TYPES: ReadonlySet<string> = new Set(['sms_otp', 'email_otp', 'totp']);

/** Whether the step is a passcode step — see `AUTH_FLOW_PASSCODE_STEP_TYPES`. */
export function isPasscodeAuthStep(step: Pick<AppAuthFlowStep, 'type'>): boolean {
  return (AUTH_FLOW_PASSCODE_STEP_TYPES as readonly string[]).includes(step.type);
}

/**
 * The control a field wants, after the realm's hints have been corrected for
 * what the step actually is.
 *
 * - `pin` — a masked box-per-digit entry. Decided by the **step type**
 *   (`AUTH_FLOW_PASSCODE_STEP_TYPES`), not by the field: the realm sends the
 *   passcode as `type: password` with `autocomplete: one-time-code`, and
 *   following either hint drew a password line or an unmasked code row
 *   (`17d40f1`). The field is still secret, so it is masked.
 * - `otp` — an unmasked box-per-digit entry: `autocomplete: one-time-code`, or
 *   any text-like field on an OTP step.
 * - `date` — `type: date`; identity verification (`verify_identity`) asks for a
 *   date of birth, and a picker is the control, not a typed field.
 * - `hidden` — `type: hidden`, or any field of a `device_signature` step: its
 *   `signature` and `deviceId` are filled from the keystore and never typed.
 * - `email`, `password`, `text` — the realm's own `type`, with `text` as the
 *   default for every hint this function does not describe.
 */
export function authFieldKind(
  field: AppAuthFlowField,
  step: Pick<AppAuthFlowStep, 'type'>,
): 'text' | 'email' | 'password' | 'otp' | 'pin' | 'date' | 'hidden' {
  if (step.type === 'device_signature' || field.type === 'hidden') return 'hidden';
  if (isPasscodeAuthStep(step) && (field.type === 'password' || field.autocomplete === 'one-time-code')) {
    return 'pin';
  }
  if (field.autocomplete === 'one-time-code') return 'otp';
  switch (field.type) {
    case 'password':
      return 'password';
    case 'email':
      return 'email';
    case 'date':
      return 'date';
    default:
      return OTP_STEP_TYPES.has(step.type) ? 'otp' : 'text';
  }
}

/** The server's words when the flow id is unknown, missing or expired — older gateways send only these. */
const FLOW_NOT_STARTED_MESSAGE = /no login flow is in progress/i;

/**
 * Whether a rejected `submit` / `selectMethod` means the login transaction is
 * gone — expired (a flow lives about thirty minutes server-side), completed or
 * cancelled elsewhere, or never started on this session (a bearer-mode flow id
 * lost to a reload on another origin).
 *
 * The remedy is not an error message: start a fresh flow once, replay, and say
 * the login timed out (DF Capital `2ea32b3`). The gateway names the reason —
 * `{ error: 'flow-not-started' }`, which the client maps to a
 * `AppOperationError` with code `invalid-payload` and
 * `details.reason === 'flow-not-started'` — and older gateways send only the
 * sentence, so the message is matched as a fallback. The error is duck-typed on
 * `name`, `code`, `details` and `message` rather than `instanceof`, because an
 * application's bundle and the SDK's can hold different `AppOperationError`
 * classes.
 */
export function isAuthFlowNotStarted(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { name?: unknown; code?: unknown; details?: unknown; message?: unknown };
  if (candidate.name === 'AppOperationError') {
    const details = candidate.details;
    if (details && typeof details === 'object' && (details as Record<string, unknown>)['reason'] === 'flow-not-started') {
      return true;
    }
  }
  return typeof candidate.message === 'string' && FLOW_NOT_STARTED_MESSAGE.test(candidate.message);
}

/** Field names and autocomplete hints that identify the customer rather than authenticate them. */
const IDENTITY_FIELD_NAMES: ReadonlySet<string> = new Set(['username', 'email', 'login', 'user']);
const IDENTITY_AUTOCOMPLETE: ReadonlySet<string> = new Set(['username', 'email']);

/**
 * The values worth carrying from one step to the next: the customer's identity,
 * and nothing else.
 *
 * When the realm issues the next step, the values typed into the previous one
 * are stale, except for the username or email — which a restart after an
 * expired flow (`isAuthFlowNotStarted`) or a "Back to login" wants prefilled.
 * The field is found **by the realm's own field name and hint** on the step
 * being rendered (`username`, `email`, `autocomplete: username|email`), not by
 * a name hard-coded in the application, because the realm decides what it is
 * called (`identify` sends `username`; a registration step may send `email`).
 *
 * Passwords, codes and passcodes never survive: a passcode or OTP step keeps
 * nothing at all, even a field the realm happens to call `username`, so a code
 * is never replayed into the step the server issued after it (DF Capital
 * `2ea32b3`, `17d40f1`).
 */
export function retainAuthIdentity(
  previous: Record<string, string>,
  step: AppAuthFlowStep,
): Record<string, string> {
  if (step.type === 'device_signature' || isPasscodeAuthStep(step) || OTP_STEP_TYPES.has(step.type)) return {};
  const retained: Record<string, string> = {};
  for (const field of step.fields) {
    const kind = authFieldKind(field, step);
    if (kind !== 'text' && kind !== 'email') continue;
    const identity =
      IDENTITY_FIELD_NAMES.has(field.name.toLowerCase()) ||
      (field.autocomplete !== undefined && IDENTITY_AUTOCOMPLETE.has(field.autocomplete));
    const value = previous[field.name];
    if (identity && typeof value === 'string' && value !== '') retained[field.name] = value;
  }
  return retained;
}
